/**
 * ContractsPage – Lists all contracts for the current user
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { NavBar, NavActions } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  FileText,
  Loader2,
  CheckCircle,
  Clock,
  Briefcase,
  User,
} from "lucide-react";

export default function ContractsPage() {
  const { token } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/contracts/my-contracts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setContracts(data.contracts);
        }
      } catch {
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <NavBar>
        <Link to="/dashboard" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">Dashboard</Link>
        <NavActions />
      </NavBar>
      <main className="flex-1 max-w-3xl mx-auto px-4 py-8 pt-24 w-full">
        <h1 className="text-2xl font-bold mb-6" data-testid="contracts-page-title">
          My Contracts
        </h1>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-white/40" />
          </div>
        ) : contracts.length === 0 ? (
          <Card className="bg-white/[0.03] border-white/10 p-10 text-center">
            <FileText className="w-10 h-10 text-white/20 mx-auto mb-3" />
            <p className="text-white/50">No contracts yet.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {contracts.map((c) => (
              <Link
                key={c.contract_id}
                to={`/contracts/${c.contract_id}`}
                data-testid={`contract-row-${c.contract_id}`}
              >
                <Card className="bg-white/[0.03] border-white/10 p-4 hover:bg-white/[0.06] transition-colors flex items-center gap-4">
                  <Avatar className="w-10 h-10 shrink-0">
                    <AvatarImage src={c.counterparty_image} />
                    <AvatarFallback className="bg-white/10 text-white text-sm">
                      {(c.counterparty_name || "?").charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {c.template_title || "Contract"}
                    </p>
                    <p className="text-sm text-white/50 truncate">
                      {c.counterparty_name} &middot; {c.job_title}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        c.contract_type === "per_job"
                          ? "bg-blue-500/20 text-blue-300"
                          : "bg-purple-500/20 text-purple-300"
                      }`}
                    >
                      {c.contract_type === "per_job" ? (
                        <Briefcase className="w-3 h-3 inline mr-1" />
                      ) : (
                        <User className="w-3 h-3 inline mr-1" />
                      )}
                      {c.contract_type === "per_job" ? "Per Job" : "Per Person"}
                    </span>
                    {c.status === "signed" ? (
                      <span className="flex items-center gap-1 text-xs text-green-400">
                        <CheckCircle className="w-3.5 h-3.5" /> Signed
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-amber-400">
                        <Clock className="w-3.5 h-3.5" /> Pending
                      </span>
                    )}
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
